import { App } from "antd";
import { getApiError, postWorkStopThunk } from "tm-shared/api";

import { useAsyncAction } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { selectUncompletedTasks } from "@/store/slices/progress";
import { addModalToQueue } from "@/store/slices/ui";
import { ModalType } from "@/types/modals";

interface UseEndWorkFlowParams {
    hasShownReminder: {
        current: {
            task: boolean;
            checkout: boolean;
        };
    };
    onSendLocation: () => Promise<void>;
    onForceStopRequired: () => void;
}

export const useEndWorkFlow = ({
    hasShownReminder,
    onSendLocation,
    onForceStopRequired,
}: UseEndWorkFlowParams) => {
    const dispatch = useAppDispatch();
    const { notification } = App.useApp();

    const role = useProtectedUserRoleSelector();
    const checkoutList = useAppSelector((s) => s.work.checkoutList);
    const uncompletedTasksCount = useAppSelector(selectUncompletedTasks).length;

    return useAsyncAction(async () => {
        let isReadyToEndWork = true;

        if (uncompletedTasksCount > 0 && !hasShownReminder.current.task) {
            hasShownReminder.current.task = true;

            dispatch(
                addModalToQueue({
                    type: ModalType.UncompletedTasks,
                    id: ModalType.UncompletedTasks,
                }),
            );
        }

        const hasUncompletedCheckout = Object.values(checkoutList).length > 0;

        if (hasUncompletedCheckout && !hasShownReminder.current.checkout) {
            hasShownReminder.current.checkout = true;
            isReadyToEndWork = false;

            dispatch(
                addModalToQueue({
                    type: ModalType.UncompletedCheckouts,
                    id: ModalType.UncompletedCheckouts,
                }),
            );
        }

        if (!isReadyToEndWork) return;

        try {
            await dispatch(
                postWorkStopThunk({ force: false, roleSlug: role }),
            ).unwrap();
        } catch (error) {
            const { displayMessage, code } = getApiError(error);

            if (code === "-32500") {
                onForceStopRequired();
                return;
            }

            notification.error({
                title: "Не вдалося завершити робочий день",
                description: displayMessage,
            });
        }

        await onSendLocation();
    });
};
